import React, { useEffect, useState, useCallback } from 'react';
import { useWatch } from 'antd/es/form/Form';
import {
  Space,
  ConfigProvider,
  Button,
  Form,
  Input,
  Row,
  Select,
  Divider,
  Image,
  Upload,
  Col,
  Checkbox,
  ColorPicker,
  Popover,
  InputNumber,
} from 'antd';
import { PlusOutlined, PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { TinyColor } from '@ctrl/tinycolor';
import ImagesUploader from '../ImagesUploader';
import ImageSelectionModal from '../ImageSelectionModal';
import { baseURL, getImageFallbackUrl } from '../../utils/helpers';
import { useFetch } from '../../hooks';
import { URLS } from '../../constants';

const { Option } = Select;
const { TextArea } = Input;

function CreateUpdateProductForm({
  selectedProductItem = {
    currency: 'IQD',
  },
  categoryList,
  subcategoryList,
  brandList,
  languageList,
  onSubmit,
  initialValues, // Additional initial values to merge
  onFormReady, // Callback to pass form instance to parent
  // Search functionality
  searchCategories = [],
  searchSubcategories = [],
  searchBrands = [],
  isSearchingCategories = false,
  isSearchingSubcategories = false,
  isSearchingBrands = false,
  onSearchCategories,
  onSearchSubcategories,
  onSearchBrands,
  addonList = [],
}) {
  const isUpdate = !!selectedProductItem?.id;
  const [imageUrl, setImageUrl] = useState();
  const [isDeliverable, setIsDeliverable] = useState();
  const [isInStock, setIsInStock] = useState();
  const [isDinning, setIsDinning] = useState();

  const [uploadFileList, setUploadFileList] = useState([]); // State in the parent to manage the file list
  const [deleteFileList, setDeleteFileList] = useState([]);
  const [mainImageError, setMainImageError] = useState('');
  const [areAdditionalImagesValid, setAreAdditionalImagesValid] = useState(true);
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [failedMainImages, setFailedMainImages] = useState(new Set()); // Track failed main images

  const [isLinkAnImageWithColorModalOpen, setIsLinkAnImageWithColorModalOpen] = useState(false);
  const [currentRowKey, setCurrentRowKey] = useState(null); // Track row key opening modal

  // User settings state
  const [userSettings, setUserSettings] = useState({
    is_showing_types: true,
    is_showing_colors: true,
    is_showing_sizes: true,
    is_showing_flavors: false,
    is_showing_addons: true,
    is_showing_subcategories: true,
    is_showing_brands: true,
    is_showing_additional_images: true,
  });

  // Track if initial form setup is complete to prevent unnecessary resets
  const [isInitialSetupComplete, setIsInitialSetupComplete] = useState(false);


  const showLinkAnImageModal = key => {
    setCurrentRowKey(key); // Store which row is opening the modal
    setIsLinkAnImageWithColorModalOpen(true);
  };

  const handleConfirmLinkAnImageWithColor = selectedImage => {
    if (currentRowKey !== null) {
      // Update the colors form with the new image link
      const currentColors = colorsForm.getFieldValue('colors') || [];
      const updatedColors = currentColors.map((color, index) =>
        index === currentRowKey ? {
          ...color,
          // Prioritize image_url (CDN/backend URL) over url (might be blob URL)
          imageLink: selectedImage.image_url || selectedImage.url,
          linkedImageId: selectedImage.uid, // Store the image ID for reference
          linkedImageFile: selectedImage.originFileObj // Store file reference for matching after upload
        } : color
      );

      colorsForm.setFieldsValue({
        colors: updatedColors
      });
    }
    console.log('Updated colors:', colorsForm.getFieldValue('colors'));
    setIsLinkAnImageWithColorModalOpen(false);
  };

  // Handle image uploaded through the modal - add it to additional images
  const handleImageUploadedThroughModal = (uploadedImage) => {
    // Add to the upload file list so it appears in the Additional Images section
    setUploadFileList(prevList => {
      // Check if image already exists
      const existingIndex = prevList.findIndex(img => img.uid === uploadedImage.uid);
      if (existingIndex === -1) {
        return [...prevList, uploadedImage];
      }
      return prevList;
    });
  };

  const handleCancelLinkAnImageWithColor = () => {
    setIsLinkAnImageWithColorModalOpen(false);
  };

  console.log(selectedProductItem);

  // Combine existing images with newly uploaded images for linking
  const existingImages = isUpdate
    ? selectedProductItem.images.map(image => ({
      uid: image.id, // Use the ID as uid to keep it unique
      name: `Image ${image.id}`, // Placeholder name
      status: 'done', // Mark these as already uploaded
      url: image.image_url || `${baseURL}/files/product-images/${selectedProductItem.id}/${image.id}`, // The link to the image
      image_url: image.image_url // Explicitly store image_url for CDN URL
    }))
    : [];

  // Include newly uploaded images (that have preview URLs) for linking
  const newlyUploadedImages = uploadFileList
    .filter(file => file.url && !file.uid.toString().startsWith('rc-upload-'))
    .map(file => ({
      uid: file.uid,
      name: file.name || 'Uploaded Image',
      status: 'done',
      url: file.url,
      image_url: file.image_url, // Preserve image_url if it exists
      originFileObj: file.originFileObj, // Preserve originFileObj for upload matching
      isStaged: file.isStaged // Preserve isStaged flag
    }));

  // Deduplicate images by checking if URL already exists in existingImages
  // This prevents showing the same image twice when it's been uploaded and then the product is reloaded
  const existingImageUrls = new Set(existingImages.map(img => img.url || img.image_url));
  const deduplicatedNewlyUploadedImages = newlyUploadedImages.filter(
    img => !existingImageUrls.has(img.url) && !existingImageUrls.has(img.image_url)
  );

  const initialImages = [...existingImages, ...deduplicatedNewlyUploadedImages];

  // Handle changes in the file list, passed to the child component
  const handleUploadFileListChange = newFileList => {
    setUploadFileList(newFileList); // Update the file list in the parent
  };

  const [productForm] = Form.useForm();
  const [typesForm] = Form.useForm();
  const [colorsForm] = Form.useForm();
  const [sizesForm] = Form.useForm();
  const [flavorsForm] = Form.useForm();

  const { doFetch: fetchUserSettings } = useFetch();

  const watchedColors = useWatch('colors', colorsForm, []); // Provide a default empty array


  // const colors1 = ['#6253E1', '#04BEFE'];
  // const colors2 = ['#fc6076', '#ff9a44', '#ef9d43', '#e75516'];
  const colors3 = ['#40e495', '#30dd8a', '#2bb673'];
  const getHoverColors = colors => colors.map(color => new TinyColor(color).lighten(5).toString());
  const getActiveColors = colors => colors.map(color => new TinyColor(color).darken(5).toString());

  // Load user settings only once on mount
  useEffect(() => {
    fetchUserSettings({
      url: URLS.userSettings,
      onSuccess: (data) => {
        setUserSettings({
          is_showing_types: data.is_showing_types !== false,
          is_showing_colors: data.is_showing_colors !== false,
          is_showing_sizes: data.is_showing_sizes !== false,
          is_showing_flavors: data.is_showing_flavors === true,
          is_showing_addons: data.is_showing_addons !== false,
          is_showing_subcategories: data.is_showing_subcategories !== false,
          is_showing_brands: data.is_showing_brands !== false,
          is_showing_additional_images: data.is_showing_additional_images !== false,
        });
      },
      onError: () => {
        // Use default settings if fetch fails
        console.warn('Failed to load user settings, using defaults');
      },
    });

    // Notify parent component that form is ready
    if (onFormReady) {
      onFormReady(productForm);
    }
  }, []); // Empty dependency array - only run once on mount

  // Handle form initialization and selectedProductItem changes
  useEffect(() => {
    // For create mode: only set values once on initial mount
    // For update mode: set values whenever selectedProductItem changes
    if (isUpdate) {
      // UPDATE MODE: Always update when selectedProductItem changes
      if (selectedProductItem) {
        selectedProductItem.image && setImageUrl(selectedProductItem.image);

        // Set main form values
        productForm.setFieldsValue({
          price: selectedProductItem.price,
          dineInPrice: selectedProductItem.dineInPrice,
          priceWholesale: selectedProductItem.priceWholesale,
          currency: selectedProductItem.currency,
          discount: selectedProductItem.discount,
          calories: selectedProductItem.calories,
          categoryId: selectedProductItem.categoryId,
          subcategoryId: selectedProductItem.subcategoryId,
          brandId: selectedProductItem.brandId,
          isDeliverable: selectedProductItem.isDeliverable,
          isInStock: selectedProductItem.isInStock,
          isDinning: selectedProductItem.isDinning,
          isRecommended: selectedProductItem.isRecommended,
          isNew: selectedProductItem.isNew,
          isSpecialOffer: selectedProductItem.isSpecialOffer,
          isTrending: selectedProductItem.isTrending,
          isBestSeller: selectedProductItem.isBestSeller,
          addonIds: selectedProductItem.addon_ids || [],
          ...selectedProductItem, // This will include the flattened translation fields
        });

        const existingTypes = typesForm.getFieldValue('types');
        if (!existingTypes || existingTypes.length === 0) {
          typesForm.setFieldsValue({ types: selectedProductItem.types });
        }

        const existingColors = colorsForm.getFieldValue('colors');
        if (!existingColors || existingColors.length === 0) {
          // Clean up any blob URLs from colors (these would be broken after page reload)
          const cleanedColors = selectedProductItem.colors?.map(color => {
            const withId = color.id ? color : { ...color, id: crypto.randomUUID() };
            if (withId.imageLink && withId.imageLink.startsWith('blob:')) {
              const cleanedColor = { ...withId };
              delete cleanedColor.imageLink;
              delete cleanedColor.linkedImageId;
              delete cleanedColor.linkedImageFile;
              return cleanedColor;
            }
            return withId;
          }) || selectedProductItem.colors;

          colorsForm.setFieldsValue({ colors: cleanedColors });
        }

        const existingSizes = sizesForm.getFieldValue('sizes');
        if (!existingSizes || existingSizes.length === 0) {
          sizesForm.setFieldsValue({
            sizes: selectedProductItem.sizes?.map(s => s.id ? s : { ...s, id: crypto.randomUUID() }),
          });
        }

        const existingFlavors = flavorsForm.getFieldValue('flavors');
        if (!existingFlavors || existingFlavors.length === 0) {
          flavorsForm.setFieldsValue({
            flavors: selectedProductItem.flavors?.map(f => f.id ? f : { ...f, id: crypto.randomUUID() }),
          });
        }

      }
      setIsInitialSetupComplete(true);
    } else if (!isInitialSetupComplete) {
      // CREATE MODE: Only set defaults once on initial mount
      const defaultValues = {
        currency: selectedProductItem?.currency || 'IQD',
        isDinning: true,
        isDeliverable: true,
        isInStock: true,
        isRecommended: false,
        isNew: false,
        isSpecialOffer: false,
        isTrending: false,
        isBestSeller: false,
        // Merge any initialValues passed from parent (e.g., pre-selected categoryId)
        ...initialValues,
      };
      productForm.setFieldsValue(defaultValues);
      setIsInitialSetupComplete(true);
    }
  }, [selectedProductItem, typesForm, colorsForm, sizesForm, flavorsForm, productForm, isUpdate, isInitialSetupComplete, initialValues]);

  function getBase64(img, callback) {
    if (!(img instanceof Blob)) return; // Guard against undefined or non-Blob
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  const handleSubmit = async values => {
    const types = await typesForm.validateFields();
    const colors = await colorsForm.validateFields();
    const sizes = await sizesForm.validateFields();
    const flavors = await flavorsForm.validateFields();

    // Get the main image file if it exists and structure it as expected by ProductCreate
    const mainImageFile = mainImageFileList.length > 0 ? mainImageFileList[0] : null;

    values = {
      ...values,
      types: types.types,
      colors: colors.colors,
      sizes: sizes.sizes,
      flavors: flavors.flavors,
      addon_ids: values.addonIds || [],
      uploadFileList: uploadFileList,
      deleteFileList: deleteFileList,
      // Structure the main image as expected by ProductCreate (image.file.originFileObj)
      image: mainImageFile ? { file: { originFileObj: mainImageFile.originFileObj } } : undefined,
    };
    onSubmit(values);
  };

  const handleImageChange = info => {
    console.log('handleImageChange called with:', info); // Debug log
    console.log('Current mainImageFileList before update:', mainImageFileList); // Debug log
    console.log('Current imageUrl before update:', imageUrl); // Debug log

    // Get the current file being processed
    const file = info.file;
    console.log('File object:', file); // Debug log
    console.log('File.originFileObj:', file?.originFileObj); // Debug log
    console.log('File.uid:', file?.uid); // Debug log
    console.log('File.status:', file?.status); // Debug log

    // Check if we have a valid file to process
    if (file && (file.originFileObj || file.uid)) {
      console.log('Processing file:', file.name, 'Size:', file.size, 'Type:', file.type); // Debug log

      // File has already been validated in beforeUpload, so just process it
      console.log('File is valid, updating state'); // Debug log
      setMainImageFileList(info.fileList);
      console.log('Updated mainImageFileList to:', info.fileList); // Debug log

      // If we have originFileObj, convert to base64 for preview
      if (file.originFileObj) {
        getBase64(file.originFileObj, result => {
          console.log('Base64 conversion successful, setting imageUrl'); // Debug log
          setImageUrl(result);
        });
      } else if (file.url || file.thumbUrl || file.preview) {
        // Use existing URL/preview if available
        console.log('Using existing URL/preview for image'); // Debug log
        setImageUrl(file.url || file.thumbUrl || file.preview);
      }
    } else if (info.fileList.length === 0) {
      // Handle file removal
      console.log('File list is empty, clearing state'); // Debug log
      setMainImageFileList([]);
      setImageUrl('');
      setMainImageError('');
    } else {
      console.log('No valid file found, file:', file); // Debug log
      console.log('info.fileList:', info.fileList); // Debug log
    }
  };

  const validateFile = file => {
    console.log('validateFile called for:', file.name, 'Size:', file.size, 'Type:', file.type); // Debug log

    const MAX_SIZE_BYTES = 0.75 * 1024 * 1024; // 0.75 MB
    const hasImageMimeType = file.type && file.type.startsWith('image/');
    const hasImageExtension = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || '');
    const isImage = hasImageMimeType || hasImageExtension;

    console.log('Validation checks:', { hasImageMimeType, hasImageExtension, isImage, fileSize: file.size, maxSize: MAX_SIZE_BYTES }); // Debug log

    if (!isImage) {
      const errorMsg = 'Only image files are allowed (jpg, png, gif, webp, bmp, svg).';
      console.log('Setting error message:', errorMsg); // Debug log
      setMainImageError(errorMsg);
      return false;
    }

    if (typeof file.size === 'number' && file.size > MAX_SIZE_BYTES) {
      const errorMsg = `Image must be 0.75 MB or smaller. Current size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`;
      console.log('Setting error message:', errorMsg); // Debug log
      setMainImageError(errorMsg);
      return false;
    }

    // File is valid - clear error message
    console.log('File validation passed, clearing error message'); // Debug log
    setMainImageError('');
    return true;
  };

  // Handle main image error - try fallback URL
  const handleMainImageError = (imageUrl) => {
    if (!failedMainImages.has(imageUrl)) {
      setFailedMainImages(prev => new Set([...prev, imageUrl]));
    }
  };

  const beforeUploadMainImage = file => {
    console.log('beforeUploadMainImage called for:', file.name, 'Size:', file.size, 'Type:', file.type); // Debug log

    // Validate file immediately when selected
    const isValid = validateFile(file);

    if (!isValid) {
      // File is invalid - prevent it from being added to the list
      console.log('File rejected in beforeUpload - invalid'); // Debug log
      return Upload.LIST_IGNORE;
    }

    // File is valid - allow it to be added to the list for preview and submission
    console.log('File accepted in beforeUpload - valid'); // Debug log
    return false; // Prevent auto-upload but allow file to be added to list
  };

  const handleMainImageRemove = file => {
    setMainImageFileList([]);
    setImageUrl('');
    setMainImageError('');
    return true;
  };

  return (
    <Form
      onFinish={handleSubmit}
      name="createUpdateProductForm"
      className="form-wrapper"
      layout="vertical"
      size="large"
      initialValues={{ ...selectedProductItem, ...initialValues }}
      form={productForm}
    >
      <Row>
        <Col span={12}>
          {languageList.map(lan => (
            <div key={lan.id}>
              <Form.Item
                name={`name.${lan.id}`}
                label={`Name in ${lan.name}`}
                rules={[{ required: true, message: `Please enter ${lan.name} product name!` }]}
              >
                <Input placeholder={`product name in ${lan.name}`} />
              </Form.Item>
              <Form.Item
                name={`description.${lan.id}`}
                label={`Description in ${lan.name}`}
                rules={[{ message: `Please enter ${lan.name} product description!` }]}
              >
                <TextArea rows={2} placeholder={`product description in ${lan.name}`} />
              </Form.Item>
              <Divider />
            </div>
          ))}
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col span={4} className="m-r-10">
          <Form.Item
            name="price"
            label="Price (Online)"
            type="number"
            rules={[{ required: true, message: 'Please enter online/delivery price!' }]}
          >
            <Input placeholder="Online / delivery price" />
          </Form.Item>
        </Col>
        <Col span={4} className="m-r-10">
          <Form.Item
            name="dineInPrice"
            label="Price (Dine-in)"
            type="number"
            rules={[{ required: false }]}
          >
            <Input placeholder="Dine-in price" />
          </Form.Item>
        </Col>
        <Col span={4} className="m-r-10">
          <Form.Item
            name="priceWholesale"
            label="Price (Wholesale)"
            type="number"
            rules={[{ required: false }]}
          >
            <Input placeholder="Wholesale price of the product" />
          </Form.Item>
        </Col>
        <Col span={4} className="m-r-10">
          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true, message: 'Please enter product currency!' }]}
          >
            <Select placeholder="Select a currency">
              <Option value="USD">USD</Option>
              <Option value="IQD">IQD</Option>
              <Option value="EUR">EUR</Option>
              <Option value="SAR">SAR</Option>
              <Option value="SYP">SYP</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            name="discount"
            label="Discount"
            type="number"
            rules={[{ required: false }]}
          >
            <InputNumber min={0} max={100} defaultValue={0} placeholder="Discount" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            name="calories"
            label="Calories"
            type="number"
            rules={[{ required: false }]}
          >
            <InputNumber min={0} defaultValue={0} placeholder="Calories" />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <Form.Item
            name="isInStock"
            valuePropName='checked'
          >
            <Checkbox defaultChecked={true}>Is In Stock</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isDeliverable" valuePropName="checked">
            <Checkbox>Is Deliverable</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isDinning" valuePropName="checked">
            <Checkbox>Is Dinning</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isRecommended" valuePropName="checked">
            <Checkbox>Is Recommended</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isNew" valuePropName="checked">
            <Checkbox>Is New</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isSpecialOffer" valuePropName="checked">
            <Checkbox>Is Special Offer</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isTrending" valuePropName="checked">
            <Checkbox>Is Trending</Checkbox>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="isBestSeller" valuePropName="checked">
            <Checkbox>Is Best Seller</Checkbox>
          </Form.Item>
        </Col>
      </Row>
      {userSettings.is_showing_types && (
        <Row>
          <Form form={typesForm} name="dynamic_form" autoComplete="off">
            <Form.List name="types">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: 'flex', marginBottom: 20, marginTop: 0 }}
                      align="baseline"
                    >
                      {languageList.map(lan => (
                        <Form.Item
                          {...restField}
                          key={`typeName.${lan.id}`}
                          label={`Type in ${lan.name}`}
                          name={[name, `typeName.${lan.id}`]}
                          fieldKey={[fieldKey, `typeName.${lan.id}`]}
                          rules={[{ required: true, message: 'Missing input' }]}
                        >
                          <Input placeholder={`Type in ${lan.name}`} />
                        </Form.Item>
                      ))}
                      <Form.Item
                        {...restField}
                        label="Price"
                        name={[name, 'price']}
                        fieldKey={[fieldKey, 'price']}
                        rules={[{ required: true, message: 'Missing input' }]}
                      >
                        <Input placeholder="Price" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        label="Price (Wholesale)"
                        name={[name, 'priceWholesale']}
                        fieldKey={[fieldKey, 'priceWholesale']}
                        rules={[{ required: false }]}
                      >
                        <Input placeholder="Wholesale price of this type" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <ConfigProvider
                      theme={{
                        components: {
                          Button: {
                            colorPrimary: `linear-gradient(116deg,  ${colors3.join(', ')})`,
                            colorPrimaryHover: `linear-gradient(116deg, ${getHoverColors(colors3).join(', ')})`,
                            colorPrimaryActive: `linear-gradient(116deg, ${getActiveColors(colors3).join(', ')})`,
                            lineWidth: 0,
                          },
                        },
                      }}
                    >
                      <Button
                        onClick={() => add({ id: crypto.randomUUID() })}
                        type="primary"
                        size="large"
                        icon={<PlusCircleOutlined />}
                      >
                        Add New Type
                      </Button>
                    </ConfigProvider>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </Row>
      )}
      {userSettings.is_showing_colors && (
        <>
          <Divider />
          <Row>
            <Form form={colorsForm} name="dynamic_colors_form" autoComplete="off">
              <Form.List name="colors">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => {
                      // Get the image link for the current field
                      const imageLink = watchedColors?.[name]?.imageLink || '';

                      return (
                        <Space
                          key={key}
                          style={{ display: 'flex', marginBottom: 20, marginTop: 0 }}
                          align="baseline"
                        >
                          {/* Hidden field to store the color ID */}
                          <Form.Item
                            {...restField}
                            name={[name, 'id']}
                            fieldKey={[fieldKey, 'id']}
                            noStyle
                          >
                            <Input type="hidden" />
                          </Form.Item>
                          {languageList.map(lan => (
                            <Form.Item
                              {...restField}
                              key={`colorName.${lan.id}`}
                              label={`Color in ${lan.name}`}
                              name={[name, `colorName.${lan.id}`]}
                              fieldKey={[fieldKey, `colorName.${lan.id}`]}
                              rules={[{ required: true, message: 'Missing input' }]}
                            >
                              <Input placeholder={`Color in ${lan.name}`} />
                            </Form.Item>
                          ))}
                          <Form.Item
                            {...restField}
                            label="Hexcode"
                            name={[name, 'hexcode']}
                            fieldKey={[fieldKey, 'hexcode']}
                            rules={[{ required: false }]}
                          >
                            <ColorPicker
                              defaultValue="#1677ff"
                              showText
                              disabledAlpha
                              onChangeComplete={value => value.toHexString()}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Image link"
                            name={[name, 'imageLink']}
                            fieldKey={[fieldKey, 'imageLink']}
                            rules={[{ required: false }]}
                          >
                            <Input style={{ display: 'none' }} />
                            <Popover
                              content={
                                initialImages.length === 0
                                  ? 'Add images using the Additional Images section below, then return here to link them to colors.'
                                  : 'Link an existing additional image to this color.'
                              }
                            >
                              <Button
                                type="primary"
                                onClick={() => showLinkAnImageModal(key)}
                              >
                                {imageLink ? 'Change Image' : 'Link Image'}
                              </Button>
                            </Popover>
                          </Form.Item>

                          {/* Image Preview */}
                          {imageLink && (
                            <div style={{ marginLeft: '10px', marginTop: '30px' }}>
                              <Image
                                src={imageLink}
                                fallback={getImageFallbackUrl(imageLink)}
                                alt="Selected Preview"
                                style={{
                                  width: '6rem',
                                  height: '6rem',
                                  objectFit: 'contain',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                }}
                              />
                              <div style={{ fontSize: '10px', color: '#666', marginTop: '4px', textAlign: 'center' }}>
                                Linked
                              </div>
                            </div>
                          )}

                          <MinusCircleOutlined onClick={() => remove(name)} />
                        </Space>
                      );
                    })}
                    <Form.Item>
                      <ConfigProvider
                        theme={{
                          components: {
                            Button: {
                              colorPrimary: `linear-gradient(116deg,  ${colors3.join(', ')})`,
                              colorPrimaryHover: `linear-gradient(116deg, ${getHoverColors(colors3).join(', ')})`,
                              colorPrimaryActive: `linear-gradient(116deg, ${getActiveColors(colors3).join(', ')})`,
                              lineWidth: 0,
                            },
                          },
                        }}
                      >
                        <Button
                          onClick={() => add({ id: crypto.randomUUID() })}
                          type="primary"
                          size="large"
                          icon={<PlusCircleOutlined />}
                        >
                          Add New Color
                        </Button>
                      </ConfigProvider>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </Row>
        </>
      )}
      {userSettings.is_showing_sizes && (
        <>
          <Divider />
          <Row>
            <Form form={sizesForm} name="dynamic_sizes_form" autoComplete="off">
              <Form.List name="sizes">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                      <Space
                        key={key}
                        style={{ display: 'flex', marginBottom: 20, marginTop: 0 }}
                        align="baseline"
                      >
                        {/* Hidden field to store the size ID */}
                        <Form.Item
                          {...restField}
                          name={[name, 'id']}
                          fieldKey={[fieldKey, 'id']}
                          noStyle
                        >
                          <Input type="hidden" />
                        </Form.Item>
                        {languageList.map(lan => (
                          <Form.Item
                            {...restField}
                            key={`sizeName.${lan.id}`}
                            label={`Size in ${lan.name}`}
                            name={[name, `sizeName.${lan.id}`]}
                            fieldKey={[fieldKey, `sizeName.${lan.id}`]}
                            rules={[{ required: true, message: 'Missing input' }]}
                          >
                            <Input placeholder={`Size in ${lan.name}`} />
                          </Form.Item>
                        ))}
                        <Form.Item
                          {...restField}
                          label="Price"
                          name={[name, 'price']}
                          fieldKey={[fieldKey, 'price']}
                          rules={[{ required: false, message: 'Missing input' }]}
                        >
                          <Input placeholder="Price" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="Price (Wholesale)"
                          name={[name, 'priceWholesale']}
                          fieldKey={[fieldKey, 'priceWholesale']}
                          rules={[{ required: false }]}
                        >
                          <Input placeholder="Wholesale price of this size" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="Discount"
                          name={[name, 'discount']}
                          fieldKey={[fieldKey, 'discount']}
                          type="number"
                          rules={[{ required: false }]}
                        >
                          <InputNumber min={0} max={100} defaultValue={0} placeholder="Discount" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Form.Item>
                      <ConfigProvider
                        theme={{
                          components: {
                            Button: {
                              colorPrimary: `linear-gradient(116deg,  ${colors3.join(', ')})`,
                              colorPrimaryHover: `linear-gradient(116deg, ${getHoverColors(colors3).join(', ')})`,
                              colorPrimaryActive: `linear-gradient(116deg, ${getActiveColors(colors3).join(', ')})`,
                              lineWidth: 0,
                            },
                          },
                        }}
                      >
                        <Button
                          onClick={() => add({ id: crypto.randomUUID() })}
                          type="primary"
                          size="large"
                          icon={<PlusCircleOutlined />}
                        >
                          Add New Size
                        </Button>
                      </ConfigProvider>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </Row>
        </>
      )}
      {userSettings.is_showing_flavors && (
        <>
          <Divider />
          <Row>
            <Form form={flavorsForm} name="dynamic_flavors_form" autoComplete="off">
              <Form.List name="flavors">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                      <Space
                        key={key}
                        style={{ display: 'flex', marginBottom: 20, marginTop: 0 }}
                        align="baseline"
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'id']}
                          fieldKey={[fieldKey, 'id']}
                          noStyle
                        >
                          <Input type="hidden" />
                        </Form.Item>
                        {languageList.map(lan => (
                          <Form.Item
                            {...restField}
                            key={`flavorName.${lan.id}`}
                            label={`Flavor in ${lan.name}`}
                            name={[name, `flavorName.${lan.id}`]}
                            fieldKey={[fieldKey, `flavorName.${lan.id}`]}
                            rules={[{ required: true, message: 'Missing input' }]}
                          >
                            <Input placeholder={`Flavor in ${lan.name}`} />
                          </Form.Item>
                        ))}
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Form.Item>
                      <ConfigProvider
                        theme={{
                          components: {
                            Button: {
                              colorPrimary: `linear-gradient(116deg,  ${colors3.join(', ')})`,
                              colorPrimaryHover: `linear-gradient(116deg, ${getHoverColors(colors3).join(', ')})`,
                              colorPrimaryActive: `linear-gradient(116deg, ${getActiveColors(colors3).join(', ')})`,
                              lineWidth: 0,
                            },
                          },
                        }}
                      >
                        <Button
                          onClick={() => add({ id: crypto.randomUUID() })}
                          type="primary"
                          size="large"
                          icon={<PlusCircleOutlined />}
                        >
                          Add New Flavor
                        </Button>
                      </ConfigProvider>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </Row>
        </>
      )}
      {userSettings.is_showing_addons && (
        <>
          <Divider />
          <Row>
            <Col span={24}>
              <Form.Item
                name="addonIds"
                label="Linked addons"
                tooltip="Create addons under Addons in the menu, then select them here."
              >
                <Select
                  mode="multiple"
                  placeholder="Select addons"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={addonList.map(a => ({
                    value: a.id,
                    label:
                      a.addon_translations?.find(t => t.language_id === languageList[0]?.id)?.name_text ||
                      a.addon_translations?.[0]?.name_text ||
                      `Addon #${a.id}`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {/* <Row>
        <Col span={12}>
          <ConfigProvider
            theme={{
              components: {
                Button: {
                  colorPrimary: `linear-gradient(116deg,  ${colors3.join(', ')})`,
                  colorPrimaryHover: `linear-gradient(116deg, ${getHoverColors(colors3).join(', ')})`,
                  colorPrimaryActive: `linear-gradient(116deg, ${getActiveColors(colors3).join(', ')})`,
                  lineWidth: 0,
                },
              },
            }}
          >
            <Button type="primary" size="large" icon={<PlusCircleOutlined />}>
              Add New Type
            </Button>
          </ConfigProvider>
        </Col>
      </Row> */}
      {true && (
        <>
          <Divider />
          <Row>
            <Col span={12}>
              <Form.Item
                label="Category"
                name="categoryId"
                help="Type to search for a category"
                rules={[{ required: true, message: 'Please select the category!' }]}
              >
                <Select
                  placeholder="Select a category"
                  showSearch
                  filterOption={false}
                  loading={isSearchingCategories}
                  onSearch={onSearchCategories}
                  notFoundContent={isSearchingCategories ? 'Searching...' : 'No categories found'}
                >
                  {/* Show search results if available, otherwise show original list */}
                  {searchCategories.length > 0
                    ? searchCategories.map(cat => (
                        <Option value={cat.id} key={`search-${cat.id}`}>
                          {cat.translations.map((translation, index) => (
                            <>
                              {translation.lanName}: <b>{translation.name}</b>{' '}
                              {index !== cat.translations.length - 1 && '- '}
                            </>
                          ))}
                        </Option>
                      ))
                    : categoryList.map(cat => (
                        <Option value={cat.id} key={`original-${cat.id}`}>
                          {cat.translations.map((translation, index) => (
                            <>
                              {translation.lanName}: <b>{translation.name}</b>{' '}
                              {index !== cat.translations.length - 1 && '- '}
                            </>
                          ))}
                        </Option>
                      ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {userSettings.is_showing_subcategories && (
        <>
          <Divider />
          <Row>
            <Col span={12}>
              <Form.Item
                label="Subcategory"
                name="subcategoryId"
                help="Type to search for a subcategory"
                rules={[{ required: false, message: 'Please select the subcategory!' }]}
              >
                <Select
                  placeholder="Select a subcategory"
                  showSearch
                  filterOption={false}
                  loading={isSearchingSubcategories}
                  onSearch={onSearchSubcategories}
                  notFoundContent={isSearchingSubcategories ? 'Searching...' : 'No subcategories found'}
                >
                  {/* Show search results if available, otherwise show original list */}
                  {searchSubcategories.length > 0
                    ? searchSubcategories.map(subcat => (
                        <Option value={subcat.id} key={`search-${subcat.id}`}>
                          {subcat.translations.map((translation, index) => (
                            <>
                              {translation.lanName}: <b>{translation.name}</b>{' '}
                              {index !== subcat.translations.length - 1 && '- '}
                            </>
                          ))}
                        </Option>
                      ))
                    : subcategoryList.map(subcat => (
                        <Option value={subcat.id} key={`original-${subcat.id}`}>
                          {subcat.translations.map((translation, index) => (
                            <>
                              {translation.lanName}: <b>{translation.name}</b>{' '}
                              {index !== subcat.translations.length - 1 && '- '}
                            </>
                          ))}
                        </Option>
                      ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {userSettings.is_showing_brands && (
        <>
          <Divider />
          <Row>
            <Col span={12}>
              <Form.Item
                label="Brand"
                name="brandId"
                rules={[{ required: false }]}
                help="Type to search for a brand"
              >
                <Select
                  placeholder="Select a brand"
                  showSearch
                  filterOption={false}
                  loading={isSearchingBrands}
                  onSearch={onSearchBrands}
                  notFoundContent={isSearchingBrands ? 'Searching...' : 'No brands found'}
                >
                  {/* Show search results if available, otherwise show original list */}
                  {searchBrands.length > 0
                    ? searchBrands.map(brd => (
                        <Option value={brd.id} key={`search-${brd.id}`}>
                          {brd.translations.map((translation, index) => (
                            <>
                              {translation.lanName}: <b>{translation.name}</b>{' '}
                              {index !== brd.translations.length - 1 && '- '}
                            </>
                          ))}
                        </Option>
                      ))
                    : brandList.map(brd => (
                        <Option value={brd.id} key={`original-${brd.id}`}>
                          {brd.translations.map((translation, index) => (
                            <>
                              {translation.lanName}: <b>{translation.name}</b>{' '}
                              {index !== brd.translations.length - 1 && '- '}
                            </>
                          ))}
                        </Option>
                      ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      <Divider />
      <Row>
        <Col span={12}>
          <Form.Item label="Product Image:" name="image" className="product-image-upload">
            <Upload
              listType="picture-card"
              fileList={mainImageFileList}
              onPreview={file => console.log(file)}
              onChange={handleImageChange}
              beforeUpload={beforeUploadMainImage}
              onRemove={handleMainImageRemove}
              accept="image/*"
              maxCount={1}
              showUploadList={true}
            >
              {imageUrl ? (
                <Image
                  src={failedMainImages.has(imageUrl) ? getImageFallbackUrl(imageUrl) : imageUrl}
                  alt="avatar"
                  style={{ width: '100%' }}
                  preview={false}
                  onError={() => handleMainImageError(imageUrl)}
                />
              ) : (
                <>
                  <PlusOutlined type="plus" />
                  <div className="ant-upload-text">Upload</div>
                </>
              )}
            </Upload>
            {mainImageError ? (
              <div style={{ color: '#ff4d4f', marginTop: 8 }}>{mainImageError}</div>
            ) : null}
          </Form.Item>
        </Col>
      </Row>

      {userSettings.is_showing_additional_images && (
        <>
          <Divider />
          <Row>
            <Col span={12}>
              <Form.Item label="Additional Images:" className="product-image-upload">
                <ImagesUploader
                  fileList={uploadFileList} // Pass the uploadFileList to the child component
                  setFileList={handleUploadFileListChange} // Pass the callback to update uploadFileList
                  initialImages={initialImages}
                  deletedImages={deleteFileList}
                  setDeletedImages={setDeleteFileList}
                  onValidationChange={setAreAdditionalImagesValid}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {/* Link an image with color modal */}

      <ImageSelectionModal
        visible={isLinkAnImageWithColorModalOpen}
        images={initialImages}
        onSelect={handleConfirmLinkAnImageWithColor}
        onCancel={handleCancelLinkAnImageWithColor}
        productId={selectedProductItem?.id}
        isCreateMode={!isUpdate}
        colorIndex={currentRowKey}
        onImageUploaded={handleImageUploadedThroughModal}
      />

      <Row className="m-t-30 f-r">
        <Form.Item>
          <Button type="primary" htmlType="submit" size="large" disabled={!!mainImageError || !areAdditionalImagesValid}>
            Save
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
}

export default CreateUpdateProductForm;
